import { useMemo } from 'react';
import { format, differenceInCalendarDays } from 'date-fns';
import { __ } from '@wordpress/i18n';
import { useBookingStore } from '../store/useBookingStore';
import { useService } from './useBookingComputed';
import { formatFullDate } from '../utils/date-time';

/**
 * Hook to format the original booking for display in entry edit mode.
 * Takes into account service settings like treatAsNights, blockSize, etc.
 */
export function useOriginalBookingDisplay(): string | null {
	const originalRange = useBookingStore((state) => state.originalRange);
	const { data: service } = useService();

	return useMemo(() => {
		if (!originalRange?.from || !service) {
			return null;
		}

		const { from, to } = originalRange;
		const isFullDay = service.blockSize >= 1440;
		const treatAsNights = service.treatAsNights;

		// Format date
		const startDateStr = formatFullDate(from);

		// Single date/time booking
		if (!to || from.getTime() === to.getTime()) {
			if (isFullDay) {
				return startDateStr;
			}
			const timeStr = format(from, 'h:mm a');
			return `${startDateStr} ${timeStr}`;
		}

		// Date range booking
		const endDateStr = formatFullDate(to);
		const isSameDay =
			from.getFullYear() === to.getFullYear() &&
			from.getMonth() === to.getMonth() &&
			from.getDate() === to.getDate();

		if (isFullDay) {
			if (treatAsNights) {
				const nights = differenceInCalendarDays(to, from);
				return `${__('Check-in:', 'gp-bookings')} ${startDateStr}, ${__('Check-out:', 'gp-bookings')} ${endDateStr} (${nights} ${nights === 1 ? __('night', 'gp-bookings') : __('nights', 'gp-bookings')})`;
			}
			const days = differenceInCalendarDays(to, from) + 1;
			return `${startDateStr} - ${endDateStr} (${days} ${days === 1 ? __('day', 'gp-bookings') : __('days', 'gp-bookings')})`;
		}

		// Time-based booking with range
		const startTimeStr = format(from, 'h:mm a');
		const endTimeStr = format(to, 'h:mm a');

		if (isSameDay) {
			return `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
		}

		return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
	}, [originalRange, service]);
}
